import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, BarChart3, Plus, ReceiptText, Users, Wallet } from 'lucide-react';
import { cash, type CashAccount, type CashEntry } from '../api';
import { useReadOnly } from '../ui/AppShell';
import { CashShell } from './CashShell';
import { ActionQueueCard, MetricStrip, TimelineRow } from '../ui/mobileOps';
import { Button, EmptyState, Pill, Spinner } from '../ui/primitives';
import { SectionCard } from '../ui/components';
import { absDecimal, formatInr, toneFromBalance } from './format';

export function CashHomePage() {
  const nav = useNavigate();
  const readOnly = useReadOnly();

  const accountsQ = useQuery<CashAccount[]>({
    queryKey: ['cash-accounts-active'],
    queryFn: () => cash.listAccounts({ status: 'active' }),
  });
  const balancesQ = useQuery<Array<{ cashAccountId: number; balance: string }>>({
    queryKey: ['cash-balances'],
    queryFn: () => cash.listBalances(),
  });
  const recentQ = useQuery<CashEntry[]>({
    queryKey: ['cash-entries-recent'],
    queryFn: () => cash.listEntries({ limit: 10 }),
  });

  const accountsById = useMemo(
    () => new Map((accountsQ.data ?? []).map((account) => [account.id, account])),
    [accountsQ.data],
  );

  const topAccounts = useMemo(() => {
    const balanceById = new Map((balancesQ.data ?? []).map((item) => [item.cashAccountId, item.balance]));
    return (accountsQ.data ?? [])
      .filter((account) => account.status === 'active')
      .map((account) => ({ account, balance: balanceById.get(account.id) ?? '0' }))
      .sort((left, right) => absDecimal(right.balance) - absDecimal(left.balance))
      .slice(0, 5);
  }, [accountsQ.data, balancesQ.data]);

  const peopleCount = (accountsQ.data ?? []).filter((account) => account.status === 'active').length;
  const netBalance = useMemo(
    () => (balancesQ.data ?? []).reduce((sum, item) => sum + Number(item.balance || '0'), 0),
    [balancesQ.data],
  );
  const hasAccounts = (accountsQ.data ?? []).length > 0;
  const primaryAccount = topAccounts[0]?.account ?? null;

  return (
    <CashShell
      subtitle="Recent movement and people balances in one place"
      actions={
        !readOnly ? (
          <Button size="sm" onClick={() => nav('/cash/accounts')}>
            <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
            <span>People</span>
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4">
        <div className="order-2 md:order-1">
          {!accountsQ.isLoading && !balancesQ.isLoading && (
            <MetricStrip
            section="cash"
            items={[
              { label: 'People', value: String(peopleCount), note: 'active ledgers', to: '/cash/accounts' },
              {
                label: 'Net cash',
                value: formatInr(netBalance === 0 ? '0' : netBalance.toFixed(2)),
                note: 'across people',
                tone: netBalance < 0 ? 'neg' : netBalance > 0 ? 'pos' : 'default',
              },
              { label: 'Recent', value: String((recentQ.data ?? []).length), note: 'latest entries' },
            ]}
            />
          )}
        </div>

        <div className="order-1 md:order-2 grid gap-3 lg:grid-cols-[1.1fr_0.9fr]">
          <ActionQueueCard
            section="cash"
            eyebrow="Direct action"
            title={primaryAccount ? `Record against ${primaryAccount.name}` : 'Add your first cash person'}
            description={
              primaryAccount
                ? 'Cash entry happens inside a person ledger. Keep the busiest one one tap away.'
                : 'Cash becomes useful once you create the first active person ledger.'
            }
            actions={
              <>
                {primaryAccount ? (
                  <Button size="sm" onClick={() => nav(`/cash/accounts/${primaryAccount.id}/ledger`)}>
                    Open ledger
                  </Button>
                ) : (
                  !readOnly && (
                    <Button size="sm" onClick={() => nav('/cash/accounts')}>
                      Add person
                    </Button>
                  )
                )}
                <Button size="sm" variant="secondary" onClick={() => nav('/cash/accounts')}>
                  Manage people
                </Button>
              </>
            }
          />

          <ActionQueueCard
            section="cash"
            eyebrow="Focus"
            title={
              netBalance === 0
                ? 'Cash is balanced'
                : netBalance > 0
                ? 'More money is with people'
                : 'You have more payouts recorded'
            }
            description="Recent movement and the largest balances are stacked below for fast thumb scanning."
            tone="neutral"
          />
        </div>

        {accountsQ.isFetched && !hasAccounts && (
          <SectionCard section="cash">
            <p className="text-small text-ink-muted">
              No people yet.{' '}
              <Link to="/cash/accounts" className="font-medium text-sec-cash hover:underline">
                Add your first one
              </Link>
            </p>
          </SectionCard>
        )}

        <div className="grid gap-4 lg:grid-cols-2">
          <SectionCard
            section="cash"
            title="Recent cash movement"
            action={
              <Link to="/cash/accounts" className="text-caption font-medium text-sec-cash hover:underline">
                All people
              </Link>
            }
          >
            {recentQ.isLoading ? (
              <div className="flex items-center gap-2 text-small text-ink-muted">
                <Spinner /> Loading…
              </div>
            ) : (recentQ.data ?? []).length === 0 ? (
              <EmptyState
                section="cash"
                icon={ReceiptText}
                title="No entries yet"
                description="Add money in or out on a person to see activity here."
              />
            ) : (
              <div className="space-y-3">
                {(recentQ.data ?? []).map((entry) => {
                  const account = accountsById.get(entry.cashAccountId);
                  const reversed = entry.reversesEntryId !== null;
                  return (
                    <Link key={entry.id} to={`/cash/accounts/${entry.cashAccountId}/ledger`} className="block">
                      <TimelineRow
                        section="cash"
                        title={account?.name ?? `#${entry.cashAccountId}`}
                        subtitle={entry.note?.trim() || (entry.direction === 'in' ? 'Money received' : 'Money sent')}
                        date={entry.effectiveDate}
                        amount={`${entry.direction === 'in' ? 'In' : 'Out'} ${formatInr(entry.amount)}`}
                        amountTone={entry.direction === 'in' ? 'pos' : 'warn'}
                        badges={reversed ? <Pill tone="neutral">Reversal</Pill> : undefined}
                        action={<ArrowRight className="ml-auto h-4 w-4 text-ink-muted" strokeWidth={1.75} />}
                      />
                    </Link>
                  );
                })}
              </div>
            )}
          </SectionCard>

          <SectionCard
            section="cash"
            title="People to watch"
            action={
              <Link to="/cash/accounts" className="text-caption font-medium text-sec-cash hover:underline">
                Manage
              </Link>
            }
          >
            {accountsQ.isLoading || balancesQ.isLoading ? (
              <div className="flex items-center gap-2 text-small text-ink-muted">
                <Spinner /> Loading…
              </div>
            ) : topAccounts.length === 0 ? (
              <EmptyState
                section="cash"
                icon={BarChart3}
                title="No people yet"
                description="Add people to see their balances here."
              />
            ) : (
              <div className="space-y-3">
                {topAccounts.map(({ account, balance }) => (
                  <Link key={account.id} to={`/cash/accounts/${account.id}/ledger`} className="block">
                    <TimelineRow
                      section="cash"
                      title={account.name}
                      subtitle={account.status}
                      date="Current balance"
                      amount={formatInr(balance)}
                      amountTone={
                        balance.startsWith('-') ? 'neg' : balance === '0' ? 'default' : 'pos'
                      }
                      action={<ArrowRight className="ml-auto h-4 w-4 text-ink-muted" strokeWidth={1.75} />}
                    />
                  </Link>
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      </div>
    </CashShell>
  );
}
